import React, { useCallback, useMemo } from 'react';
import EventLabel from '../../../components/EventLabel/EventLabel';
import AutoComplete from '../../../components/AutoComplete';
import { useStore } from '../../../store/store';
import { observer } from 'mobx-react-lite';

interface IBookingDetailsProps {
  comment: string;
  hotelGroupId: number;
  onBookingChange: (booking: any, hotelGroupId: number) => void;
  orders: any[];
  orderRowId: number;
  hotel: any;
  dateRangeString: string;
  allRooms: any[];
  type: string;
  checkHeights: any[];
  nights: number;
  imageUrl: string;
  eventId: number;
  rooms: any[];
  price: number;
  orderId: number;
  currency: string;
  displayEventSettings?: any;
  vatPercent?: number;
  bookings: any[];
  roomMates: any[];
  selectRoomMateFromOrder?: boolean;
  hideRegisterLater?: boolean;
  onRoomChange: (room: any, hotelGroupId: number, remove?: boolean) => void;
}

function BookingDetails(props: IBookingDetailsProps) {
  const {
    orders,
    orderRowId,
    hotel,
    allRooms,
    comment,
    eventId,
    bookings,
    hotelGroupId,
    rooms = [],
    roomMates = [],
    selectRoomMateFromOrder = false,
    orderId,
    onBookingChange,
    onRoomChange,
    hideRegisterLater = false,
  } = props;
  const { roomSize } = hotel;

  const order = useMemo(() => orders?.find(o => o.leader) ?? orders?.[0], [orders, orderId]);

  const isBookedInRoom = useCallback(
    (orderId: number) => {
      return rooms?.some(orh => orh.orderId === orderId);
    },
    [rooms]
  );

  const isBooked = useCallback(
    (orderId: number) => {
      return allRooms?.some(orh => orh.orderId === orderId);
    },
    [allRooms]
  );

  const handleRoomMateChange = useCallback(
    (name: string, index: number) => {
      // Remove the room mate if name is empty
      if (!name) {
        return onRoomChange(rooms[index], hotelGroupId, true);
      }
      // Add the room mate if the room is empty
      if (!rooms[index]) {
        return onRoomChange({ external: false, orderId: null, roomMate: name, eventId, orderRowId }, hotelGroupId);
      }

      // Alway null the orderId when changing the room mate to free text
      return onRoomChange({ ...rooms[index], roomMate: name, orderId: null }, hotelGroupId);
    },
    [onRoomChange, hotelGroupId, orderRowId, rooms]
  );

  const setLeaderLivingInRoom = useCallback(() => {
    const isLeaderBooked = isBooked(order.orderId);

    if (isLeaderBooked) {
      const previousBooking = allRooms.find(orh => orh.orderId === order.orderId);
      const previousHotelGroupId = orders.flatMap(o => o.orderRow).find(or => or.orderRowId === previousBooking?.orderRowId)?.hotelGroupId;

      if (previousBooking && previousHotelGroupId) {
        onRoomChange(previousBooking, previousHotelGroupId, true);
      }

      // If you uncheck the current leader on the booking row do not add the leader to another room until explicitly added
      if (previousHotelGroupId === hotelGroupId) {
        return;
      }
    }

    const roomMateName = order?.person?.firstName && order?.person.lastName ? `${order?.person?.firstName} ${order?.person?.lastName}` : '';
    const emptyIndex = roomSize > rooms.length ? roomSize - rooms?.length + rooms?.length : 0;

    const roomMate = rooms[emptyIndex];

    return onRoomChange({ ...roomMate, orderId, external: false, roomMate: roomMateName }, hotelGroupId);
  }, [order, isBooked]);

  const handleOrderOnRoomChange = useCallback(
    (orderId: number, index?: number | null) => {
      if (!orderId) return;
      const add = !isBookedInRoom(orderId);

      if (add) {
        const isLeader = orders.find(o => o.orderId === orderId)?.leader ?? orders?.[0];

        // If the room is full remove the first room mate
        if (rooms.length === roomSize) {
          const roomMateToRemove = rooms[0];
          onRoomChange(roomMateToRemove, hotelGroupId, true);
        }

        // check if the leader is added in another room, and remove it

        const previousBooking = allRooms.find(orh => orh.orderId === orderId);
        const previousHotelGroupId = orders.flatMap(o => o.orderRow).find(or => or.orderRowId === previousBooking?.orderRowId)?.hotelGroupId;

        if (isLeader || previousBooking) {
          //Remove the leader from the room if booked
          if (previousBooking && previousHotelGroupId) {
            onRoomChange(previousBooking, previousHotelGroupId, true);
          }
        }

        // Add the order to the room
        const order = orders.find(o => o.orderId === orderId) ?? roomMates.find(o => o.orderId === orderId);
        const roomMateName = order?.person?.firstName && order?.person.lastName ? `${order?.person?.firstName} ${order?.person?.lastName}` : '';
        const roomMate = rooms[index as number];

        if (index !== undefined && index !== null && roomMate) {
          return onRoomChange({ ...roomMate, orderId, external: false, roomMate: roomMateName }, hotelGroupId);
        }

        return onRoomChange({ orderId, external: false, roomMate: roomMateName }, hotelGroupId);
      } else {
        const roomId = rooms.find(r => r.orderId === orderId)?.roomId;
        return onRoomChange({ roomId }, hotelGroupId, true);
      }
    },
    [orders, hotelGroupId, onRoomChange, orderId, order, isBookedInRoom, rooms]
  );

  const handleExternalRoomChange = useCallback(
    (index: number) => {
      const roomMate = rooms[index];
      onRoomChange({ ...roomMate, external: !roomMate.external }, hotelGroupId);
    },
    [rooms, hotelGroupId, onRoomChange]
  );

  return (
    <div className="flex flex-col p-4 border border-divider rounded-bl-md rounded-br-md border-t-0">
      <div className="mb-4 bg-sky-50 flex gap-x-2 px-3 py-2 rounded-lg text-xs font-medium w-fit text-sky-900 border border-sky-100">
        <i className="ri-information-line text-sm text-sky-500 font-semibold" />
        <div className="flex items-center">
          <EventLabel name="AddPeopleToRoomDescription" groupName="CMS.Hotel" />
        </div>
      </div>

      <div className="mb-4 ">
        <span>
          <span className="font-semibold mr-1">
            {order?.person?.firstName} {order?.person?.lastName}
          </span>
          <EventLabel name="LivingInRoom" groupName="CMS.Hotel" />
        </span>
        <button className="bg-transparent border-none p-0 mx-2" onClick={setLeaderLivingInRoom}>
          {isBookedInRoom(order?.orderId) ? <i className="ri-checkbox-fill ri-lg" /> : <i className="ri-checkbox-blank-line ri-lg" />}
        </button>
      </div>
      {(roomSize > 1 || (roomSize === 1 && !isBookedInRoom(order?.orderId))) && (
        <div className="mb-4 w-full">
          <div className="mb-4 font-semibold">
            <EventLabel name="RoomMates" groupName="CMS.Hotel" />
          </div>
          <div>
            {new Array(roomSize).fill(0)?.map((_, index) => (
              <div className="flex  w-full  item-center gap-x-2 mb-4" key={`roomMate_${hotelGroupId}_${index + 1}`}>
                <div className="flex items-center gap-x-2 w-[300px] lg:w-1/2">
                  <span className="text-xs w-8">#{index + 1}</span>
                  <AutoComplete
                    onInput={name => handleRoomMateChange(name, index)}
                    onChange={order => handleOrderOnRoomChange(order?.id, index)}
                    className="w-full [&>div]:w-full"
                    disabled={rooms?.[index] && isBookedInRoom(orders?.find(o => o.leader && rooms?.[index].orderId === o.orderId)?.orderId)}
                    value={rooms?.[index]?.roomMate ? { id: rooms?.[index]?.orderId, name: rooms?.[index]?.roomMate } : null}
                    inputPrepend={
                      (rooms?.[index] && isBookedInRoom(orders?.find(o => o.leader && rooms?.[index].orderId === o.orderId)?.orderId)) ||
                      !selectRoomMateFromOrder ? null : (
                        <i className="ri-expand-up-down-line" />
                      )
                    }
                    options={[
                      ...orders
                        ?.filter(order => !isBooked(order.orderId))
                        .map((order: any) => ({
                          id: order.orderId,
                          name: `${order?.person?.firstName} ${order?.person?.lastName}`,
                        })),
                      ...(selectRoomMateFromOrder
                        ? roomMates?.map((roomMate: any) => ({
                            id: roomMate.orderId,
                            name: `${roomMate?.person?.firstName} ${roomMate?.person?.lastName}`,
                          }))
                        : []),
                    ]}
                  />
                </div>

                {!hideRegisterLater &&
                  rooms?.[index] &&
                  !orders?.some(o => o.orderId === rooms?.[index]?.orderId) &&
                  !roomMates?.some(o => o.orderId === rooms?.[index]?.orderId) && (
                    <div className="flex -ml-1 md:ml-1 gap-x-1 items-center w-fit">
                      <button className="bg-transparent border-none p-0 mx-1" onClick={() => handleExternalRoomChange(index)}>
                        {rooms?.[index]?.external === false ? <i className="ri-checkbox-fill ri-lg" /> : <i className="ri-checkbox-blank-line ri-lg" />}
                      </button>
                      <span className="font-normal">
                        <EventLabel name="WillRegisterLater" groupName="CMS.Hotel" />
                      </span>
                    </div>
                  )}
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="py-2">
        <div className="mb-2 font-semibold">
          <EventLabel name="OtherRequestsForRoom" groupName="CMS.Hotel" />{' '}
        </div>
        <div className="border-divider border flex rounded-md w-full md:w-1/2 lg:w-1/2">
          <textarea
            value={comment}
            className="resize-none p-2  rounded-md h-fit w-full focus:outline-none font-normal"
            rows={1}
            onChange={e => onBookingChange({ comment: e.target.value }, hotelGroupId)}
          />
        </div>
      </div>
    </div>
  );
}

export default observer(BookingDetails);
